'use client';

import { useState, useEffect } from 'react';
import { MarketPreview } from '@/components/component/market-preview';
import { motion } from 'framer-motion';

export function MarketplacePreview() {
  const [isVisible, setIsVisible] = useState(false);

  // Animation variants
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const containerVariants = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Check if element is in viewport for animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('marketplace-preview-section');
    if (section) observer.observe(section);

    return () => {
      if (section) observer.unobserve(section);
    };
  }, []);

  return (
    <motion.main
      className="w-full relative py-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Enhanced background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 -z-10" />

      {/* Glowing dots pattern */}
      <div
        className="absolute inset-0 opacity-10 -z-5"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.3) 1px, transparent 0)',
          backgroundSize: '20px 20px',
        }}
      />

      {/* Additional decorative elements */}
      <div className="absolute left-0 top-1/4 w-full h-96 -z-1">
        <motion.div
          className="absolute -left-20 w-64 h-64 rounded-full bg-blue-500/5 blur-3xl"
          animate={{
            x: [0, 50, 0],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
        />
        <motion.div
          className="absolute right-20 top-40 w-80 h-80 rounded-full bg-indigo-600/5 blur-3xl"
          animate={{
            x: [0, -30, 0],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: 'reverse',
            delay: 2,
          }}
        />
      </div>

      <section
        id="marketplace-preview-section"
        className="min-h-screen flex w-full justify-center"
      >
        <motion.div
          className="w-full max-w-7xl mx-auto px-4 md:px-8"
          variants={containerVariants}
          initial="initial"
          animate={isVisible ? 'animate' : 'initial'}
        >
          <motion.div
            className="mb-16 text-center relative"
            variants={fadeInUp}
          >
            <div className="absolute -inset-10 bg-blue-500/10 blur-[100px] opacity-50 rounded-full"></div>
            <motion.h2
              className="text-3xl md:text-5xl font-bold relative z-10 mb-6"
              style={{
                background: 'linear-gradient(to right, #ffffff, #93c5fd)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Discover Our Marketplace
            </motion.h2>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto">
              Explore our curated collection of luxury timepieces from trusted
              dealers worldwide
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-full">
            <MarketPreview />
          </motion.div>
        </motion.div>
      </section>
    </motion.main>
  );
}
